export const navLinks = [
  { label: "Home", to: "/" },
  { label: "Market", to: "/market-orders" },
  { label: "Limit", to: "/limit-orders" },
  { label: "Stop", to: "/stop-orders" },
  { label: "Stop-Limit", to: "/stop-limit-orders" },
  { label: "Execution", to: "/order-execution" },
  { label: "Glossary", to: "/glossary" },
];

export const footerColumns = [
  {
    title: "Order Types",
    links: [
      { label: "Market Orders", to: "/market-orders" },
      { label: "Limit Orders", to: "/limit-orders" },
      { label: "Stop Orders", to: "/stop-orders" },
      { label: "Stop-Limit Orders", to: "/stop-limit-orders" },
    ],
  },
  {
    title: "Learn",
    links: [
      { label: "Order Execution & Slippage", to: "/order-execution" },
      { label: "Glossary", to: "/glossary" },
      { label: "Home", to: "/" },
    ],
  },
];

export const faqs = [
  {
    q: "What is the most common forex order type?",
    a: "The market order is the most common — it fills instantly at the best available price, which is why most trades are opened with one. Its trade-off is that the price is never guaranteed.",
  },
  {
    q: "What is the difference between a stop order and a stop-limit order?",
    a: "Both wait for a trigger price. The difference is what happens next: a stop order becomes a market order and may fill at any price, while a stop-limit order becomes a limit order and will never fill worse than your chosen limit — but it may not fill at all in a fast move.",
  },
  {
    q: "Should I always use a stop-loss?",
    a: "For leveraged forex trading, yes — a stop-loss is basic risk management. It defines the maximum loss you are willing to accept before you even enter. Whether you use a plain stop or a stop-limit depends on whether you value a guaranteed exit or a guaranteed price more.",
  },
  {
    q: "What happens to my order when the market gaps?",
    a: "A gap skips over prices, so a market or stop order can fill well past your level. A stop-limit protects you from that bad fill but risks not being filled at all. This is the core trade-off every trader must weigh.",
  },
];

export const glossaryTerms = [
  {
    term: "Pip",
    category: "Basics",
    definition:
      "The smallest standard price move in most currency pairs, usually the fourth decimal place (0.0001). For JPY pairs it is the second decimal place. On EUR/USD one pip is about $10 on a standard lot — the figure behind every cost quoted on this site.",
  },
  {
    term: "Bid / Ask",
    category: "Basics",
    definition:
      "The bid is the price at which you can sell; the ask is the price at which you can buy. You always buy at the higher ask and sell at the lower bid, and the gap between the two is the spread.",
  },
  {
    term: "Lot",
    category: "Basics",
    definition:
      "The standard unit of size. One standard lot is 100,000 units of the base currency, a mini is 10,000 and a micro is 1,000 — which is why one pip is worth roughly $10, $1 or $0.10 depending on the size you trade.",
  },
  {
    term: "Long / Short",
    category: "Basics",
    definition:
      "Long means you bought, expecting the price to rise; short means you sold a currency you did not own, expecting it to fall. In forex every position is long one currency and short the other at the same time.",
  },
  {
    term: "Major / Minor / Exotic",
    category: "Basics",
    definition:
      "Majors are the most traded pairs, always against the dollar (EUR/USD, USD/JPY). Minors are crosses without the dollar (EUR/GBP). Exotics pair a major with a smaller economy — same mechanics, far wider spreads and far less depth.",
  },
  {
    term: "Spread",
    category: "Costs",
    definition:
      "The difference between the bid (sell) price and the ask (buy) price. It is the built-in cost of every market order, and it is quoted before you trade.",
  },
  {
    term: "Commission",
    category: "Costs",
    definition:
      "A fee charged per lot traded, on top of the spread. It is usually quoted as a round-turn figure covering entry and exit together, which is why it does not grow when the spread widens.",
  },
  {
    term: "Swap / Rollover",
    category: "Costs",
    definition:
      "Overnight interest charged or paid for holding a position past the daily rollover, usually 17:00 New York. The rate is known in advance; the total depends on how many nights you hold.",
  },
  {
    term: "Markup",
    category: "Costs",
    definition:
      "An addition a broker applies on top of the raw interbank spread, so the spread you see is wider than the market's. It is folded into the price rather than billed separately, which is why two 'zero commission' accounts can cost very different amounts.",
  },
  {
    term: "Pip Value",
    category: "Costs",
    definition:
      "What one pip is worth in your account currency at your size. It is about $10 per standard lot on most pairs, but it shifts with the pair: on USD/JPY it depends on the yen rate, and on crosses on the quote currency's own rate.",
  },
  {
    term: "Market Order",
    category: "Order types",
    definition:
      "An instruction to buy or sell immediately at the best available price. It fills almost instantly, but the price is not guaranteed — the gap between the price you saw and the price you got is slippage.",
  },
  {
    term: "Limit Order",
    category: "Order types",
    definition:
      "An instruction to buy or sell only at your price or better. Price is guaranteed, the fill is not: if the market never reaches your level, nothing happens at all.",
  },
  {
    term: "Stop Order",
    category: "Order types",
    definition:
      "An order that sits dormant until price touches your trigger, then becomes a market order. Used to cap a loss or to enter on a breakout — the exit is guaranteed, the price is not.",
  },
  {
    term: "Stop-Limit Order",
    category: "Order types",
    definition:
      "A stop that places a limit order once triggered, instead of a market order. It guarantees the worst price you can get, but in a fast move it can leave you unfilled with the position still open.",
  },
  {
    term: "Trigger Price",
    category: "Order types",
    definition:
      "The level at which a stop or stop-limit order activates. A stop then goes to the market as a market order; a stop-limit instead places a limit order at your chosen price.",
  },
  {
    term: "Limit Price",
    category: "Order types",
    definition:
      "The worst price you are willing to accept on a limit order. Fills happen at this price or better, which is why a limit order can never slip against you.",
  },
  {
    term: "OCO",
    category: "Order types",
    definition:
      "One-Cancels-Other. Two linked orders held as a pair, where the execution of one automatically cancels the other — so a single idea cannot fill you twice.",
  },
  {
    term: "Time in Force",
    category: "Order types",
    definition:
      "How long an order stays alive. GTC rests until you cancel it, DAY expires at the session close, IOC fills what it can at once and cancels the rest, FOK cancels the whole order unless it fills completely. Most platforms default to one and never say which.",
  },
  {
    term: "Stop Entry",
    category: "Order types",
    definition:
      "A stop used to open a trade rather than close one: it triggers when price breaks a level, so you buy above the market or sell below it. The mechanism is identical to a stop-loss — only the intent differs, which is why one screen can produce both.",
  },
  {
    term: "Market-if-Touched (MIT)",
    category: "Order types",
    definition:
      "An order that becomes a market order when price touches your level, but from the other side: you buy below the market or sell above it. The mirror image of a stop entry, and the reason the two are so often mixed up.",
  },
  {
    term: "Slippage",
    category: "Execution",
    definition:
      "The difference between the price you expected and the price you actually got. Most likely with market and stop orders during fast or thin markets, and the one cost you can only manage rather than calculate.",
  },
  {
    term: "Liquidity",
    category: "Execution",
    definition:
      "How easily an order can be filled without moving the price. Major pairs during active sessions are highly liquid; exotic pairs and off-hours are not, which is where spreads widen and slippage grows.",
  },
  {
    term: "Fill / Partial Fill",
    category: "Execution",
    definition:
      "The moment an order is matched and becomes a position. A partial fill means only part of your size was matched — common with limit orders in a thin book, and the reason you can end up holding half a position.",
  },
  {
    term: "Order Book / Market Depth",
    category: "Execution",
    definition:
      "The stack of resting bids and asks at each price, and the size sitting on each of them. It shows how much you can trade before price starts to move — liquidity made concrete at one moment in time.",
  },
  {
    term: "Requote",
    category: "Execution",
    definition:
      "A broker declining your requested price and offering a new one instead, usually because the market moved between your click and the fill. Common with market orders in fast conditions — each requote costs time and often a worse price.",
  },
  {
    term: "Latency",
    category: "Execution",
    definition:
      "The delay between your order leaving the platform and reaching the venue that matches it. It matters most when price is moving, because every millisecond of delay is another chance for the market to move away from you.",
  },
  {
    term: "Stop-Loss",
    category: "Risk",
    definition:
      "An order that automatically closes a losing position at a predefined level to cap the loss. Usually a stop order that becomes a market order once triggered.",
  },
  {
    term: "Take-Profit",
    category: "Risk",
    definition:
      "An order that automatically closes a winning position at a target level. Usually a limit order, so it never fills worse than your target — but it may never fill at all.",
  },
  {
    term: "Trailing Stop",
    category: "Risk",
    definition:
      "A stop that moves with price as it goes in your favour, keeping a fixed distance so profits are protected as the trade runs. It ratchets forward and never back.",
  },
  {
    term: "Leverage",
    category: "Risk",
    definition:
      "Control of a large position with a small deposit. It magnifies both profits and losses, which is why order types, stops and position size matter so much more than the entry itself.",
  },
  {
    term: "Margin",
    category: "Risk",
    definition:
      "The deposit your broker holds to keep a leveraged position open — not a fee, and not the cost of the trade. At 1:30 leverage it is about 3.3% of the position's notional value, and you get it back when the position closes.",
  },
  {
    term: "Margin Call",
    category: "Risk",
    definition:
      "A warning that your equity has fallen close to the margin you must maintain; if it drops below, the broker starts closing positions for you. It is the point where leverage stops being a multiplier and becomes a deadline.",
  },
];

export const riskDisclaimer =
  "Educational content only. Trading forex on margin carries a high level of risk and may not be suitable for all investors. Never trade money you cannot afford to lose.";